import { useEffect, useState, useCallback } from "react";
import { tasksApi } from "@/lib/api-client";
import { AGENT_META, relativeTime } from "@/lib/format";
import { Calendar as CalIcon, CheckSquare, X, Clock, Plus, Rocket, FileText, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const PRIORITY_COLOR = { critical: "#EF4444", high: "#F59E0B", medium: "#3B82F6", low: "#71717A" };

export default function TaskBoard({ refreshKey }) {
  const [tasks, setTasks] = useState([]);
  const [view, setView] = useState("list");
  const [proofTask, setProofTask] = useState(null);
  const [snippet, setSnippet] = useState("");
  const [artifactKind, setArtifactKind] = useState("url");
  const [artifactRef, setArtifactRef] = useState("");
  const [viewerDoc, setViewerDoc] = useState(null);
  const [viewerBusy, setViewerBusy] = useState(null);
  const [mailOpen, setMailOpen] = useState(false);
  const [mailTo, setMailTo] = useState("");
  const [mailNote, setMailNote] = useState("");
  const [mailBusy, setMailBusy] = useState(false);

  const sendArtifactMail = async () => {
    if (!mailTo.trim() || !viewerDoc?.task_id) return;
    setMailBusy(true);
    try {
      const r = await tasksApi.emailArtifact(viewerDoc.task_id, { to: mailTo.trim(), note: mailNote.trim() });
      toast.success(`Artifact delivered to ${r.to} via ${r.gateway}`);
      setMailOpen(false);
      setMailTo("");
      setMailNote("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email gateway failed — not delivered");
    } finally { setMailBusy(false); }
  };

  const openArtifact = async (t) => {
    setViewerBusy(t.id);
    try {
      const a = await tasksApi.readArtifact(t.id);
      if (a.kind === "url") {
        window.open(a.url, "_blank", "noopener");
      } else {
        setViewerDoc({ ...a, task_id: t.id });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open artifact");
    } finally { setViewerBusy(null); }
  };

  const load = useCallback(async () => {
    try {
      const items = await tasksApi.list();
      setTasks(items);
    } catch (e) { console.error("tasks load failed", e); }
  }, []);

  useEffect(() => { load(); }, [refreshKey, load]);

  const exportArtifact = async (format) => {
    if (!viewerDoc?.task_id) return;
    try {
      const res = await tasksApi.downloadArtifact(viewerDoc.task_id, format);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const name = (viewerDoc.title || "artifact").replace(/[^A-Za-z0-9._-]+/g, "_").slice(0, 60);
      a.href = url;
      a.download = `${name}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Artifact exported as .${format.toUpperCase()}`);
    } catch {
      toast.error("Export failed");
    }
  };

  const complete = async (t, snippetOverride, artifactOverride) => {
    try {
      const body = { status: "completed" };
      if (snippetOverride) body.result_snippet = snippetOverride;
      if (artifactOverride) body.artifact_ref = artifactOverride;
      await tasksApi.update(t.id, body);
      toast.success("Task completed");
      setProofTask(null);
      setSnippet("");
      setArtifactRef("");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (e?.response?.status === 400 && (detail.includes("Proof of Work") || detail.includes("Artifact Mandate"))) {
        setProofTask(t);
      } else {
        console.error("task complete failed", e);
        toast.error(detail || "Complete failed");
      }
    }
  };
  const dismiss = async (t) => {
    await tasksApi.update(t.id, { status: "dismissed" });
    load();
  };

  const pending = tasks.filter((t) => t.status === "pending");
  const done = tasks.filter((t) => t.status !== "pending");

  // Calendar grouping
  const byDay = pending.reduce((acc, t) => {
    const d = new Date(t.due_date || Date.now()).toDateString();
    (acc[d] ||= []).push(t);
    return acc;
  }, {});
  const days = Object.keys(byDay).sort((a, b) => new Date(a) - new Date(b));

  return (
    <section className="uc-card p-6 uc-fade" data-testid="task-board">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="uc-chip mb-2"><CalIcon size={11}/> MASTER CALENDAR</div>
          <h2 className="font-serif-display text-2xl">Daily Workflow</h2>
          <p className="text-white/50 text-xs mt-1">Agents auto-populate deadlines and briefings here.</p>
        </div>
        <Tabs value={view} onValueChange={setView} className="w-auto">
          <TabsList className="bg-transparent border border-white/10 rounded-none p-0.5 h-auto">
            <TabsTrigger data-testid="tab-list" value="list" className="rounded-none data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-400 text-white/60 font-mono-uc text-[10px] uppercase tracking-widest px-3 py-1.5">List</TabsTrigger>
            <TabsTrigger data-testid="tab-calendar" value="calendar" className="rounded-none data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-400 text-white/60 font-mono-uc text-[10px] uppercase tracking-widest px-3 py-1.5">Calendar</TabsTrigger>
            <TabsTrigger data-testid="tab-done" value="done" className="rounded-none data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-400 text-white/60 font-mono-uc text-[10px] uppercase tracking-widest px-3 py-1.5">Completed</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <Tabs value={view} onValueChange={setView}>
        <TabsContent value="list" className="mt-0">
          <div className="space-y-2 max-h-96 overflow-y-auto uc-scroll -mr-2 pr-2">
            {pending.map((t) => <TaskRow key={t.id} t={t} onComplete={complete} onDismiss={dismiss} onArtifact={openArtifact} artifactBusy={viewerBusy}/>)}
            {pending.length === 0 && <EmptyState/>}
          </div>
        </TabsContent>
        <TabsContent value="calendar" className="mt-0">
          <div className="space-y-4 max-h-96 overflow-y-auto uc-scroll -mr-2 pr-2">
            {days.length === 0 && <EmptyState/>}
            {days.map((d) => (
              <div key={d}>
                <div className="font-mono-uc text-[10px] uppercase tracking-widest text-amber-400 mb-2 flex items-center gap-2">
                  <CalIcon size={10}/>{new Date(d).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
                  <span className="text-white/30">· {byDay[d].length} task{byDay[d].length !== 1 ? "s" : ""}</span>
                </div>
                <div className="space-y-1.5">
                  {byDay[d].map((t) => <TaskRow key={t.id} t={t} onComplete={complete} onDismiss={dismiss} onArtifact={openArtifact} artifactBusy={viewerBusy} compact/>)}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="done" className="mt-0">
          <div className="space-y-1.5 max-h-96 overflow-y-auto uc-scroll -mr-2 pr-2">
            {done.map((t) => (
              <div key={t.id} className="p-2.5 border border-white/5 bg-black/40 flex items-center gap-2">
                <span className="text-white/50 text-sm line-through flex-1 truncate">{t.title}</span>
                {t.artifact_status === "delivered" && (
                  <button
                    data-testid={`artifact-open-${t.id}`}
                    onClick={() => openArtifact(t)}
                    className="font-mono-uc text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 flex items-center gap-1 flex-none"
                  >
                    {viewerBusy === t.id ? <Loader2 size={9} className="animate-spin"/> : <FileText size={9}/>} Artifact
                  </button>
                )}
              </div>
            ))}
            {done.length === 0 && <EmptyState label="Nothing completed yet."/>}
          </div>
        </TabsContent>
      </Tabs>

      {/* Artifact Viewer — one click from any Artifact Delivered badge */}
      <Dialog open={!!viewerDoc} onOpenChange={(v) => { if (!v) { setViewerDoc(null); setMailOpen(false); } }}>
        <DialogContent className="max-w-2xl rounded-none border border-emerald-500/30 bg-[#0A0A0A] text-white" aria-describedby={undefined} data-testid="artifact-viewer">
          <DialogHeader>
            <div className="uc-chip mb-1 w-fit">
              <FileText size={11} className="text-emerald-400"/> ARTIFACT · {(viewerDoc?.kind || "").toUpperCase()}
            </div>
            <DialogTitle className="font-serif-display text-2xl leading-tight">{viewerDoc?.title || "Artifact"}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-3 flex-wrap text-[10px] font-mono-uc uppercase tracking-wider text-white/40">
            {viewerDoc?.category && <span>Category: {viewerDoc.category}</span>}
            {viewerDoc?.draft_type && <span>Type: {viewerDoc.draft_type}</span>}
            {viewerDoc?.created_at && <span>{new Date(viewerDoc.created_at).toLocaleString()}</span>}
            {viewerDoc?.signature && (
              <span className="text-emerald-400 flex items-center gap-1"><ShieldCheck size={10}/> Signed · {viewerDoc.signature.name}</span>
            )}
          </div>
          <div className="bg-black border border-white/10 p-4 max-h-[50vh] overflow-y-auto uc-scroll mt-2" data-testid="artifact-viewer-content">
            <pre className="whitespace-pre-wrap text-[12px] text-white/80 font-sans leading-relaxed">{viewerDoc?.content}</pre>
          </div>
          {viewerDoc?.source_id && (
            <p className="text-[10px] text-white/35 font-mono-uc mt-1">Source: {viewerDoc.source_kind}:{String(viewerDoc.source_id).slice(0, 12)}…</p>
          )}
          <div className="flex gap-2 mt-3">
            <button
              data-testid="artifact-export-txt"
              onClick={() => exportArtifact("txt")}
              className="flex-1 h-10 font-mono-uc text-[10px] uppercase tracking-widest text-white/70 hover:text-white border border-white/15 hover:border-white/30 flex items-center justify-center gap-1.5"
            >
              <FileText size={11}/> .TXT
            </button>
            <button
              data-testid="artifact-export-pdf"
              onClick={() => exportArtifact("pdf")}
              className="flex-1 h-10 font-mono-uc text-[10px] uppercase tracking-widest text-amber-400 hover:text-amber-200 border border-amber-500/40 hover:bg-amber-500/10 flex items-center justify-center gap-1.5"
            >
              <FileText size={11}/> .PDF
            </button>
            <button
              data-testid="artifact-send-gmail"
              onClick={() => setMailOpen((o) => !o)}
              className="flex-1 h-10 font-mono-uc text-[10px] uppercase tracking-widest text-emerald-400 hover:text-emerald-200 border border-emerald-500/40 hover:bg-emerald-500/10 flex items-center justify-center gap-1.5"
            >
              <Rocket size={11}/> Send via Gmail
            </button>
          </div>
          {mailOpen && (
            <div className="mt-2 space-y-2 border border-emerald-500/20 bg-black p-3" data-testid="artifact-mail-form">
              <input
                data-testid="artifact-mail-to"
                type="email"
                value={mailTo}
                onChange={(e) => setMailTo(e.target.value)}
                placeholder="client@company.com"
                className="w-full h-9 bg-[#121212] border border-white/10 focus:border-emerald-500/40 outline-none px-2 text-[12px] text-white/90 font-mono-uc"
              />
              <input
                data-testid="artifact-mail-note"
                value={mailNote}
                onChange={(e) => setMailNote(e.target.value)}
                placeholder="Optional note to the client…"
                className="w-full h-9 bg-[#121212] border border-white/10 focus:border-emerald-500/40 outline-none px-2 text-[12px] text-white/90"
              />
              <button
                data-testid="artifact-mail-send"
                onClick={sendArtifactMail}
                disabled={mailBusy || !mailTo.trim()}
                className="w-full h-9 font-mono-uc text-[10px] uppercase tracking-widest text-emerald-400 hover:text-emerald-200 border border-emerald-500/40 hover:bg-emerald-500/10 disabled:opacity-40 flex items-center justify-center gap-1.5"
              >
                {mailBusy ? <Loader2 size={11} className="animate-spin"/> : <Rocket size={11}/>}
                {mailBusy ? "Dispatching…" : "Deliver Artifact"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!proofTask} onOpenChange={(v) => { if (!v) { setProofTask(null); setSnippet(""); } }}>
        <DialogContent className="max-w-md rounded-none border border-white/15 bg-[#0A0A0A]" data-testid="proof-of-work-dialog">
          <DialogHeader>
            <div className="uc-chip mb-2 w-fit"><Rocket size={11}/> PROOF OF WORK · REQUIRED</div>
            <DialogTitle className="font-serif-display text-2xl">{proofTask?.title}</DialogTitle>
          </DialogHeader>
          <p className="text-white/50 text-xs mb-3">
            Artifact Mandate — attach a linked artifact <span className="text-amber-300">OR</span> a result snippet
            (Executor tasks always require one; every other task now requires one too).
          </p>
          <div className="mb-3">
            <label className="font-mono-uc text-[10px] uppercase tracking-widest text-white/50 block mb-1">Artifact Ref (optional)</label>
            <div className="flex gap-2">
              <select
                data-testid="artifact-kind"
                value={artifactKind}
                onChange={(e) => setArtifactKind(e.target.value)}
                className="w-28 h-9 rounded-none bg-black border border-white/10 font-mono-uc text-xs px-2"
              >
                <option value="url">URL</option>
                <option value="pdf">PDF</option>
                <option value="email">Email</option>
                <option value="vault">Vault Doc</option>
                <option value="infrastructure">Infra Doc</option>
              </select>
              <input
                data-testid="artifact-ref"
                value={artifactRef}
                onChange={(e) => setArtifactRef(e.target.value)}
                placeholder="https://…  or  vault-doc-id  or  pi_abc123"
                className="flex-1 h-9 rounded-none bg-black border border-white/10 font-mono-uc text-sm px-2 text-white"
              />
            </div>
          </div>
          <Textarea
            data-testid="proof-of-work-input"
            value={snippet}
            onChange={(e) => setSnippet(e.target.value)}
            placeholder="Or paste a 1-line result snippet (link, summary, confirmation)…"
            className="min-h-[80px] rounded-none bg-black border-white/10 font-mono-uc text-sm"
          />
          <div className="flex gap-2 mt-4">
            <Button variant="ghost" onClick={() => { setProofTask(null); setSnippet(""); setArtifactRef(""); }} className="flex-1 rounded-none h-10 border border-white/10 text-white/70">Cancel</Button>
            <Button
              data-testid="proof-of-work-submit"
              onClick={() => complete(
                proofTask,
                snippet.trim() || null,
                artifactRef.trim() ? { kind: artifactKind, ref: artifactRef.trim() } : null,
              )}
              disabled={!snippet.trim() && !artifactRef.trim()}
              className="flex-1 rounded-none h-10 uc-btn-amber"
            >
              <CheckSquare size={12} className="mr-1"/> Mark Complete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function TaskRow({ t, onComplete, onDismiss, onArtifact, artifactBusy, compact }) {
  const meta = AGENT_META[t.source_agent] || { color: "#71717A", label: "System" };
  return (
    <div className="p-3 border border-white/10 hover:border-white/25 transition-colors group flex items-start gap-3" data-testid={`task-${t.id}`} style={{ borderLeft: `2px solid ${meta.color}` }}>
      <button data-testid={`complete-task-${t.id}`} onClick={() => onComplete(t)} className="mt-0.5 text-white/40 hover:text-emerald-400" title="Complete">
        <CheckSquare size={14}/>
      </button>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white/90">{t.title}</div>
        {t.body && !compact && <div className="text-[11px] text-white/50 mt-0.5 line-clamp-2">{t.body}</div>}
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className="font-mono-uc text-[9px] uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</span>
          {t.venture_name && (
            <span data-testid={`task-venture-${t.id}`} className="font-mono-uc text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-teal-500/40 text-teal-300">
              {t.venture_name}
            </span>
          )}
          <span className="font-mono-uc text-[9px] uppercase tracking-wider px-1.5 py-0.5 border" style={{ borderColor: PRIORITY_COLOR[t.priority], color: PRIORITY_COLOR[t.priority] }}>
            {t.priority}
          </span>
          {t.artifact_status === "delivered" ? (
            <button
              data-testid={`artifact-delivered-${t.id}`}
              onClick={() => onArtifact?.(t)}
              title="Open artifact"
              className="font-mono-uc text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 flex items-center gap-1"
            >
              {artifactBusy === t.id ? <Loader2 size={9} className="animate-spin"/> : <FileText size={9}/>} Artifact Delivered
            </button>
          ) : (
            <span data-testid={`artifact-pending-${t.id}`} className="font-mono-uc text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-amber-500/50 text-amber-400">
              Artifact Pending
            </span>
          )}
          <span className="font-mono-uc text-[10px] text-white/40 flex items-center gap-1"><Clock size={9}/> {t.due_date ? new Date(t.due_date).toLocaleDateString() : "no due"}</span>
        </div>
      </div>
      <button data-testid={`dismiss-task-${t.id}`} onClick={() => onDismiss(t)} className="text-white/30 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5">
        <X size={12}/>
      </button>
    </div>
  );
}

function EmptyState({ label = "No pending tasks. Deploy an agent to auto-populate." }) {
  return <div className="text-white/40 text-xs italic py-8 text-center">{label}</div>;
}
